import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IChartHeaderProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getCardActionRadius
} from "../../../card/components/action/stylesheet";
import {
    CardContext
} from "../../../card/context";
import ChartTableToggle from "../tableToggle";
import Loading from "../../../loading";
import Card from "../../../card";

const ChartHeader: FC<IChartHeaderProps> = ({
    isTableToggleVisible = false,
    isRefetching = false,
    customLocalize,
    customTheme,
    description,
    actions,
    title,
    value
}) => {
    const {
        controlHeights,
        typography,
        radiuses,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const cardContext = useContext(CardContext);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        title: titleDynamicStyle
    } = useStyles({
        spaces
    });

    const renderDescription = () => {
        if(!description) {
            return null;
        }

        return <Card.Description
            customTheme={customTheme}
        >
            {description}
        </Card.Description>;
    };

    const renderActions = () => {
        if(!actions && !isTableToggleVisible) {
            return null;
        }

        return <Card.Action
            customTheme={customTheme}
        >
            {actions}
            {
                isTableToggleVisible ?
                    <ChartTableToggle
                        borderRadius={getCardActionRadius({
                            inset: cardContext?.inset ?? 0,
                            controlHeights,
                            typography,
                            radiuses,
                            borders
                        })}
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                    />
                :
                    null
            }
        </Card.Action>;
    };

    return <Card.Header
        customTheme={customTheme}
        isDivided={false}
    >
        <View
            style={[
                stylesheet.title,
                titleDynamicStyle
            ]}
        >
            <Card.Title
                customTheme={customTheme}
                headingLevel={3}
            >
                {title}
            </Card.Title>
            {
                isRefetching ?
                    <Loading
                        accessibilityLabel={localize("chart-refreshing")}
                        aria-label={localize("chart-refreshing")}
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                        role="status"
                        size="small"
                        color="low"
                    />
                :
                    null
            }
        </View>
        {renderDescription()}
        {value}
        {renderActions()}
    </Card.Header>;
};
export default Object.assign(ChartHeader, {
    cardPart: "header" as const
});
